import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

const MatchAnimation = ({ onComplete }) => {
  // Auto-dismiss after 2.5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      onComplete();
    }, 2500);
    
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, x: 100 }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.8, x: 100 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="fixed bottom-8 right-8 z-50 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-2xl p-6 max-w-sm cursor-pointer"
      onClick={onComplete}
    >
      {/* Subtle glow effect */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ 
          scale: [0, 1.2, 1],
          opacity: [0, 0.4, 0]
        }}
        transition={{
          duration: 1.2,
          ease: "easeOut"
        }}
        className="absolute inset-0 bg-emerald-400/40 rounded-2xl blur-xl"
      />
      {/* Content */}
      <div className="relative z-10 flex items-center gap-4">
        {/* Check icon */}
        <motion.div
          initial={{ scale: 0, rotate: -90 }}
          animate={{ 
            scale: 1,
            rotate: 0
          }}
          transition={{
            duration: 0.5,
            delay: 0.1,
            ease: "easeOut"
          }}
          className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0"
        >
          <Icon name="Check" size={24} color="#FFFFFF" />
        </motion.div>

        {/* Text */}
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="flex-1"
        >
          <h3 className="text-lg font-bold text-white mb-1">
            Perfect Match
          </h3>
          <p className="text-sm text-emerald-50">
            98% AI Compatibility
          </p>
        </motion.div>
      </div>
      {/* Subtle close hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.5, 0] }}
        transition={{ delay: 1, duration: 2, repeat: Infinity }}
        className="absolute top-2 right-2 text-white/40 text-xs"
      >
        ✕
      </motion.div>
    </motion.div>
  );
};

const ProfileCard = ({ profile, t, isActive }) => {
  return (
    <motion.div
      className="absolute inset-0"
    >
      <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden h-full">
        {/* Content */}
        <div className="relative p-6 md:p-8 h-full overflow-y-auto">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-start gap-4">
              {/* Avatar - Circular */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className={`w-16 h-16 rounded-full ${profile?.avatarGradient} flex items-center justify-center flex-shrink-0 shadow-lg`}
              >
                <Icon name="User" size={28} color="#FFFFFF" />
              </motion.div>
              
              {/* Title and Basic Info */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="flex-1"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {profile?.title}
                </h3>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2 text-indigo-600">
                    <Icon name="Briefcase" size={16} color="#4F46E5" />
                    <span className="font-semibold">{profile?.experience}</span>
                  </div>
                  <div className="flex items-center gap-2 text-indigo-600">
                    <Icon name="Users" size={16} color="#4F46E5" />
                    <span>{profile?.teamSize}</span>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Match Percentage - Exact design from screenshot: green bubble with percentage */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.4, type: 'spring', stiffness: 150 }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 rounded-full border border-emerald-200"
            >
              <Icon name="TrendingUp" size={14} color="#10B981" />
              <span className="text-sm font-bold text-emerald-600">
                {profile?.matchPercentage}%
              </span>
            </motion.div>
          </div>

          {/* Industries */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-6"
          >
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Building" size={16} color="#6B7280" />
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">INDUSTRIES</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {profile?.industries?.map((industry, idx) => (
                <motion.span
                  key={idx}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 + idx * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  className="px-3 py-1.5 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-lg text-sm font-medium"
                >
                  {industry}
                </motion.span>
              ))}
            </div>
          </motion.div>

          {/* Professional Profile */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 mb-6 border border-blue-100"
          >
            <div className="flex items-center gap-2 mb-3">
              <Icon name="FileText" size={16} color="#4F46E5" />
              <h4 className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">PROFESSIONAL PROFILE</h4>
            </div>
            <p className="text-gray-700 leading-relaxed text-sm">
              {profile?.description}
            </p>
          </motion.div>

          {/* Key Skills */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mb-6"
          >
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Zap" size={16} color="#6B7280" />
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">KEY SKILLS</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {profile?.skills?.map((skill, idx) => (
                <motion.span
                  key={idx}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.9 + idx * 0.05 }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 font-medium hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                >
                  {skill}
                </motion.span>
              ))}
            </div>
          </motion.div>

          {/* Key Achievements */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="mb-6"
          >
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Award" size={16} color="#6B7280" />
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">KEY ACHIEVEMENTS</h4>
            </div>
            <div className="space-y-2.5">
              {profile?.achievements?.map((achievement, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.1 + idx * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="Check" size={12} color="#10B981" />
                  </div>
                  <span className="text-sm text-gray-600">{achievement}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Status Badge */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3 }}
            className="pt-4 border-t border-gray-200"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-sm font-semibold text-emerald-700">
                Available on Market
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

const ProfileCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [showMatch, setShowMatch] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const { t } = useLanguage();

  // Updated profiles to match the CEO profile from screenshot
  const profiles = [
    {
      matchPercentage: 95,
      avatarGradient: 'bg-gradient-to-br from-indigo-600 to-blue-700',
      title: 'Chief Financial Officer',
      experience: '20+ Experience',
      teamSize: '300+ employees',
      industries: ['Finance', 'Technology'],
      description: 'Strategic finance executive with deep expertise in capital markets and public company operations. Successfully navigated multiple funding rounds and IPO process. Expert in building financial infrastructure for high-growth companies.',
      skills: ['Financial Strategy', 'IPO Experience', 'Risk Management', 'FP&A', 'Treasury'],
      achievements: [
        'Led IPO process ($500M valuation)',
        'Raised $200M+ in funding rounds',
        'Implemented financial controls at scale'
      ]
    },
    {
      matchPercentage: 92,
      avatarGradient: 'bg-gradient-to-br from-indigo-600 to-blue-700',
      title: 'VP of Construction',
      experience: '18+ Experience',
      teamSize: '150+ employees',
      industries: ['Construction', 'Real Estate'],
      description: 'Seasoned construction executive with extensive experience managing large-scale commercial and residential projects. Expert in project delivery, cost optimization, and safety compliance. Proven track record in leading multi-disciplinary teams across complex builds.',
      skills: ['Project Management', 'Budget Control', 'Safety Compliance', 'Vendor Relations', 'Quality Assurance'],
      achievements: [
        'Delivered $500M+ in construction projects',
        'Reduced project costs by 25% through optimization',
        'Maintained zero safety incidents across 50+ sites'
      ]
    },
    {
      matchPercentage: 98,
      avatarGradient: 'bg-gradient-to-br from-indigo-600 to-blue-700',
      title: 'IT Director',
      experience: '15+ Experience',
      teamSize: '80+ employees',
      industries: ['Technology', 'Healthcare'],
      description: 'Technology leader specializing in enterprise IT infrastructure and digital transformation. Expert in cybersecurity, cloud migration, and IT operations. Known for implementing robust systems that support business growth while maintaining security and compliance.',
      skills: ['IT Infrastructure', 'Cybersecurity', 'Cloud Migration', 'Team Leadership', 'ITIL/Compliance'],
      achievements: [
        'Led cloud migration for 5,000+ users',
        'Reduced IT costs by 35% through automation',
        'Achieved SOC 2 and ISO 27001 compliance'
      ]
    }
  ];

  // Automatic elegant swipe sequence
  useEffect(() => {
    if (isAnimating) return;

    setIsAnimating(true);
    
    const sequence = async () => {
      // Wait 3s to let users see first profile
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // First swipe - Pass
      setSwipeDirection('left');
      await new Promise(resolve => setTimeout(resolve, 600));
      setCurrentIndex(1);
      setSwipeDirection(null);
      
      // Wait 3.5s to see second profile
      await new Promise(resolve => setTimeout(resolve, 3500));
      
      // Second swipe - Pass
      setSwipeDirection('left');
      await new Promise(resolve => setTimeout(resolve, 600));
      setCurrentIndex(2);
      setSwipeDirection(null);
      
      // Wait 3.5s to see third profile
      await new Promise(resolve => setTimeout(resolve, 3500));
      
      // Final swipe - Match
      setSwipeDirection('right');
      await new Promise(resolve => setTimeout(resolve, 600));
      setShowMatch(true);
    };
    
    sequence();
  }, []);

  const handleMatchComplete = () => {
    setShowMatch(false);
    setSwipeDirection(null);
    // Reset to first profile immediately to ensure a card is always visible
    setCurrentIndex(0);
    setIsAnimating(false);
  };

  const getSwipeAnimation = () => {
    if (swipeDirection === 'left') {
      return {
        x: -1000,
        rotate: -25,
        opacity: 0,
        transition: { duration: 0.5, ease: [0.32, 0, 0.67, 0] }
      };
    }
    if (swipeDirection === 'right') {
      return {
        x: 1000,
        rotate: 25,
        opacity: 0,
        transition: { duration: 0.5, ease: [0.32, 0, 0.67, 0] }
      };
    }
    return {
      x: 0,
      rotate: 0,
      opacity: 1
    };
  };

  return (
    <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('profileCarousel.sectionTitle')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-4">
            {t('profileCarousel.sectionSubtitle')}
          </p>
          {/* Guidance text */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-full"
          >
            <Icon name="Info" size={16} color="#4F46E5" />
            <span className="text-sm text-indigo-700 font-medium">
              Watch the AI matching process in action
            </span>
          </motion.div>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          {/* Card Stack */}
          <div className="relative" style={{ height: '700px' }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={swipeDirection ? getSwipeAnimation() : {
                  scale: 1,
                  opacity: 1,
                  y: 0,
                  x: 0,
                  rotate: 0,
                  transition: { duration: 0.4, ease: "easeOut" }
                }}
                className="absolute inset-0"
              >
                <ProfileCard 
                  profile={profiles?.[currentIndex]} 
                  t={t} 
                  isActive={true}
                />
                
                {/* Elegant swipe overlays */}
                {swipeDirection === 'left' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="absolute top-16 right-16 bg-red-500/95 backdrop-blur-sm text-white px-6 py-3 rounded-xl font-bold text-2xl rotate-12 border-2 border-white/50 shadow-2xl"
                  >
                    PASS
                  </motion.div>
                )}
                
                {swipeDirection === 'right' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="absolute top-16 left-16 bg-emerald-500/95 backdrop-blur-sm text-white px-6 py-3 rounded-xl font-bold text-2xl -rotate-12 border-2 border-white/50 shadow-2xl"
                  >
                    MATCH
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Next card preview */}
            {!swipeDirection && currentIndex < profiles?.length - 1 && (
              <motion.div
                initial={{ scale: 0.96, opacity: 0.3 }}
                animate={{ scale: 0.96, opacity: 0.3 }}
                className="absolute inset-0 pointer-events-none"
                style={{ zIndex: -1, top: '8px' }}
              >
                <div className="bg-white rounded-2xl shadow-lg h-full" />
              </motion.div>
            )}
          </div>

          {/* Progress Indicator */}
          <div className="flex justify-center gap-3 mt-8">
            {profiles?.map((_, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0.8 }}
                animate={{ 
                  scale: index === currentIndex ? 1.2 : 0.8,
                  opacity: index === currentIndex ? 1 : 0.3
                }}
                transition={{ duration: 0.3 }}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'w-8 bg-gradient-to-r from-indigo-600 to-indigo-500 shadow-lg' 
                    : 'w-2 bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Info Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-8 text-center"
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="inline-flex items-center gap-2 px-5 py-3 bg-white rounded-full border border-indigo-200 shadow-md"
          >
            <Icon name="Sparkles" size={18} color="#4F46E5" />
            <span className="text-sm text-gray-700 font-medium">
              AI Matching with company job descriptions
            </span>
          </motion.div>
        </motion.div>
      </div>
      {/* Match Animation Overlay */}
      <AnimatePresence>
        {showMatch && <MatchAnimation onComplete={handleMatchComplete} />}
      </AnimatePresence>
    </section>
  );
};

export default ProfileCarousel;